import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Flex,
  Form,
  Input,
  Row,
  Upload
} from 'antd';
import { PERMISSION_ROUTES, PERMISSION_ACTIONS } from 'constants/permissions';
import { useFileUpload, usePermission } from 'hooks';
import dayjs, { Dayjs } from 'dayjs';
import {
  CloseCircleOutlined,
  PlusOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { RTL_LANGUAGES } from 'constants/translations';
import {
  createUserGroupAttributes,
  fetchDoctorUserAttributes,
  DoctorUserAttributeRow
} from 'services/api/userGroup';
import { Attribute, AttributeGroup } from 'types/api/attributes';

import { useDoctorProfile } from 'pages/DoctorProfile/DoctorProfileContext';
import { CKEditor, Private } from 'components/shared';
import { StyledPage } from './styled';
import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  id: string;
  lang: string;
  pageId: string | null;
  doctorGroupId?: string;
  allowToEdit?: boolean;
  attributeGroup?: AttributeGroup | null;
}

const AdditionalFields: FC<IProps> = ({
  id,
  lang,
  pageId,
  allowToEdit,
  attributeGroup
}) => {
  const [isLastItemRemoved, setLastItemRemoved] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [userAttributes, setUserAttributes] = useState<
    DoctorUserAttributeRow[]
  >([]);

  const { checkUserPermission } = usePermission();
  const { refresh } = useDoctorProfile();
  const { message, modal } = App.useApp();
  const { upload } = useFileUpload({
    progress: true
  });

  const [form] = Form.useForm();

  useEffect(() => {
    if (!id || !attributeGroup?.id) return;
    let cancelled = false;

    const load = async () => {
      try {
        // BE now filters by group_id server-side (see BE hand-off Q5b).
        // Passing it also cuts the payload from all-doctor-attrs to
        // just this tab's rows.
        const rows = await fetchDoctorUserAttributes(
          id,
          lang,
          attributeGroup.id
        );

        if (!cancelled) setUserAttributes(rows);
      } catch {
        if (!cancelled) setUserAttributes([]);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id, pageId, lang, attributeGroup?.id]);

  const hasProfileUpdatePermission = checkUserPermission(
    PERMISSION_ROUTES.doctors,
    PERMISSION_ACTIONS.update
  );

  const resetValues = useCallback(() => {
    form.resetFields();
  }, [form]);

  useEffect(() => {
    if (pageId) {
      resetValues();
    }
  }, [pageId, resetValues]);

  const renderField = (item: Attribute, name: number) => {
    const fieldName = [name, item.id];

    switch (item.type) {
      case 'date':
        return (
          <Form.Item name={fieldName} label={item.name}>
            <DatePicker
              style={{ width: '100%' }}
              direction={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
              disabled={!allowToEdit}
            />
          </Form.Item>
        );
      case 'editor':
        return (
          <Form.Item name={fieldName} label={item.name}>
            <CKEditor language={lang} disabled={!allowToEdit} />
          </Form.Item>
        );
      case 'file':
        return (
          <Form.Item
            name={fieldName}
            label={item.name}
            valuePropName="fileList"
            getValueFromEvent={e => e?.fileList || []}
          >
            <Upload
              disabled={!hasProfileUpdatePermission || !allowToEdit}
              maxCount={1}
              customRequest={e =>
                upload(e.file as File, {
                  onUploadProgress: e.onProgress
                })
                  .then(e.onSuccess)
                  .catch(e.onError)
              }
            >
              <Button block icon={<UploadOutlined />} disabled={!allowToEdit}>
                Upload
              </Button>
            </Upload>
          </Form.Item>
        );
      default:
        return (
          <Form.Item name={fieldName} label={item.name}>
            <Input
              dir={RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr'}
              disabled={!allowToEdit}
            />
          </Form.Item>
        );
    }
  };

  const onFinish = async (data: {
    values: Record<string, string | Dayjs>[];
  }) => {
    const isOneFilled = data.values.every(item =>
      Object.values(item).some(value => !!value)
    );

    if (!isOneFilled) {
      return modal.error({
        title: 'Error',
        content:
          'Please ensure that at least one field is filled in each row or remove the row'
      });
    }

    if (!attributeGroup?.id) return;

    const transformed = data.values.map(item => {
      const values: Record<string, string | undefined> = {};
      Object.entries(item).forEach(([key, value]) => {
        if (dayjs.isDayjs(value)) {
          values[key] = value.format();

          return;
        }

        if (Array.isArray(value)) {
          values[key] = value?.[0]?.response || undefined;

          return;
        }

        values[key] = value || undefined;
      });

      return values;
    });

    try {
      setIsCreating(true);
      await createUserGroupAttributes({
        user_id: Number(id),
        group_id: Number(attributeGroup.id),
        lang,
        values: transformed
      });
      message.success('Successfully saved');
      const rows = await fetchDoctorUserAttributes(id, lang, attributeGroup.id);

      setUserAttributes(rows);
      refresh();
      setLastItemRemoved(false);
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setIsCreating(false);
    }
  };

  const initialValues = useMemo(() => {
    if (!attributeGroup?.attributes?.length || !userAttributes.length) {
      return [];
    }

    const typeByAttributeId = new Map<number, string>();
    attributeGroup.attributes.forEach(attr => {
      typeByAttributeId.set(Number(attr.id), attr.type);
    });

    // BE returns ALL of the doctor's user-group rows for the requested
    // lang across every group — attribute_id is NOT unique across
    // groups (e.g. group_id=4 and group_id=5 both have attribute_id=1),
    // so filtering only by `typeByAttributeId.has(...)` bleeds rows
    // from other groups into the current tab. Restore the group scope
    // the OLD Apollo fragment lookup (`UserGroupAttributes:${doctorGroupId}`)
    // gave implicitly by matching on group_id === current group's id.
    const groupId = Number(attributeGroup.id);
    const scoped = userAttributes.filter(
      item =>
        Number(item.group_id) === groupId &&
        typeByAttributeId.has(Number(item.attribute_id))
    );

    const values = scoped.reduce<Record<string, string | Dayjs | unknown[]>[]>(
      (acc, item) => {
        const index = item.row_index - 1;

        if (!item.value) {
          return acc;
        }

        if (!acc[index]) {
          acc[index] = {};
        }

        const type = typeByAttributeId.get(Number(item.attribute_id));

        switch (type) {
          case 'date':
            acc[index][item.attribute_id] = dayjs(item.value);
            break;
          case 'file':
            acc[index][item.attribute_id] = [
              {
                uid: item.value,
                name: item.value,
                response: item.value
              }
            ];
            break;
          default:
            acc[index][item.attribute_id] = item.value;
            break;
        }

        return [...acc];
      },
      []
    );

    return values.filter(item => item);
  }, [attributeGroup, userAttributes]);

  // antd Form applies `initialValues` only at mount — by the time
  // fetchDoctorUserAttributes resolves and initialValues recomputes,
  // the Form has already captured the empty `[]` snapshot and won't
  // re-read the prop. Push the derived values into the form
  // imperatively whenever they change so the UI reflects BE data.
  useEffect(() => {
    form.setFieldsValue({ values: initialValues });
  }, [form, initialValues]);

  return (
    <StyledPage>
      <Form
        form={form}
        layout="vertical"
        size="large"
        disabled={!hasProfileUpdatePermission}
        onFinish={onFinish}
        initialValues={{
          values: initialValues
        }}
      >
        <Form.List name="values">
          {(fields, { add, move, remove }) =>
            fields.length || isLastItemRemoved ? (
              <>
                <DragDropContext
                  onDragEnd={r =>
                    move(r.source.index, r.destination?.index || 0)
                  }
                >
                  <Droppable droppableId="droppable">
                    {dropped => (
                      <div {...dropped.droppableProps} ref={dropped.innerRef}>
                        {fields.map(({ key, name }, index) => (
                          <Draggable
                            key={`${pageId}-${name}-${key}`}
                            draggableId={`${pageId}-${name}-${key}`}
                            index={index}
                          >
                            {provided => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  marginBottom: 24,
                                  userSelect: 'none'
                                }}
                              >
                                <Card title={`#${index + 1}`}>
                                  <Button
                                    danger
                                    type="text"
                                    className="page-row-remove-button"
                                    onClick={() => {
                                      remove(index);

                                      if (!index) {
                                        setLastItemRemoved(true);
                                      }
                                    }}
                                    disabled={!allowToEdit}
                                    icon={
                                      <CloseCircleOutlined
                                        style={{ fontSize: 24 }}
                                      />
                                    }
                                  />
                                  <Row gutter={[16, 0]}>
                                    {attributeGroup?.attributes?.map(
                                      (item, attributeIndex, arr) => {
                                        const attributeNumber =
                                          attributeIndex + 1;

                                        const isLast =
                                          attributeNumber === arr.length;

                                        const isLastOdd =
                                          isLast && attributeNumber % 2 !== 0;

                                        return (
                                          <Col
                                            span={isLastOdd ? 24 : 12}
                                            key={`${pageId}-${item.id}`}
                                          >
                                            {renderField(item, name)}
                                          </Col>
                                        );
                                      }
                                    )}
                                  </Row>
                                </Card>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {dropped.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                <Flex justify="end" gap={12}>
                  <Private
                    route={PERMISSION_ROUTES.doctors}
                    action={PERMISSION_ACTIONS.update}
                  >
                    <Button
                      size="large"
                      type="default"
                      htmlType="button"
                      icon={<PlusOutlined />}
                      onClick={() => add()}
                      disabled={isCreating || !allowToEdit}
                    >
                      Add Row
                    </Button>
                    <Button
                      size="large"
                      type="primary"
                      onClick={form.submit}
                      loading={isCreating}
                      disabled={!allowToEdit}
                    >
                      Save
                    </Button>
                  </Private>
                </Flex>
              </>
            ) : (
              <Empty description="There are no fields in this section. Click the button below to add a row and start filling in the data">
                <Button
                  size="large"
                  type="default"
                  htmlType="button"
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                  disabled={isCreating || !allowToEdit}
                >
                  Add Row
                </Button>
              </Empty>
            )
          }
        </Form.List>
      </Form>
    </StyledPage>
  );
};

export default memo(AdditionalFields);
